import React from 'react';
import './Logo.css';

interface LogoProps {
  /** Size of the logo */
  size?: 'small' | 'medium' | 'large' | number;
  /** Show text alongside logo */
  showText?: boolean;
  /** Custom className */
  className?: string;
  /** Click handler */
  onClick?: () => void;
}

/**
 * Company Logo Component
 * 
 * Usage:
 * <Logo size="medium" showText />
 * <Logo size={48} />
 */
const Logo: React.FC<LogoProps> = ({ 
  size = 'medium', 
  showText = false,
  className = '',
  onClick 
}) => {
  // Determine size value
  const sizeValue = typeof size === 'number' 
    ? size 
    : size === 'small' ? 32 
    : size === 'medium' ? 48 
    : 64;

  const logoStyle: React.CSSProperties = {
    width: sizeValue,
    height: sizeValue,
    cursor: onClick ? 'pointer' : 'default',
  };

  // Try to load logo from assets directory
  // Fallback to a placeholder if logo doesn't exist
  const logoSrc = '/assets/logo/LCTF%20Profile.png';

  return (
    <div 
      className={`logo-container ${className}`}
      onClick={onClick}
      style={logoStyle}
    >
      <img 
        src={logoSrc}
        alt="Company Logo"
        className="logo-image"
        style={logoStyle}
        onError={(e) => {
          // Fallback if logo doesn't exist yet
          const target = e.target as HTMLImageElement;
          target.style.display = 'none';
          const parent = target.parentElement;
          if (parent && !parent.querySelector('.logo-placeholder')) {
            const placeholder = document.createElement('div');
            placeholder.className = 'logo-placeholder';
            placeholder.textContent = 'LCTF';
            placeholder.style.cssText = `
              width: ${sizeValue}px;
              height: ${sizeValue}px;
              display: flex;
              align-items: center;
              justify-content: center;
              background: linear-gradient(135deg, #2c3e2f 0%, #4f423a 100%);
              color: white;
              font-weight: bold;
              border-radius: 8px;
              font-size: ${sizeValue * 0.4}px;
            `;
            parent.appendChild(placeholder);
          }
        }}
      />
      {showText && (
        <span className="logo-text">LCTF</span>
      )}
    </div>
  );
};

export default Logo;
